// StaleBanner — #786. Amber banner shown when the most recent API data was
// served stale-while-revalidate: the on-prem agent was unreachable and the
// worker returned cached KV data. Surfacing this is the "stale data is not
// acceptable" requirement — otherwise cached data renders as live.
// UI_GUIDELINES §3.9 (data-age indication). Reads the api-layer staleStore
// (not a page prop) because hooks unwrap the response envelope + drop `_stale`.

import { useEffect, useState, useSyncExternalStore } from 'react';
import { subscribeStale, getStaleSnapshot } from '../../store/staleStore.js';

/** Relative age like "12s ago" / "3m ago" / "2h ago"; null for a bad/absent stamp. */
export function timeAgo(iso, now = Date.now()) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const secs = Math.max(0, Math.round((now - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.round(mins / 60)}h ago`;
}

export default function StaleBanner() {
  // getStaleSnapshot doubles as the server snapshot (SSR/tests render current state).
  const { stale, since } = useSyncExternalStore(subscribeStale, getStaleSnapshot, getStaleSnapshot);

  // Keep the age fresh while the agent stays down (no-op in SSR/tests).
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!stale) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 10_000);
    return () => clearInterval(id);
  }, [stale]);

  if (!stale) return null;

  const age = timeAgo(since);
  return (
    <div
      role="status"
      className="flex items-center gap-2 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800"
    >
      <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
      <span>
        Data may be stale — the agent is unreachable{age ? `, last updated ${age}` : ''}.
      </span>
    </div>
  );
}
